import type { DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api';

import type {
  AxiosLikeInstance,
  AxiosLikeRequestConfig,
  BackstageServiceAuthLike,
} from './types';

/**
 * Injects `Authorization: Bearer <token>` from Backstage's identity API onto
 * every axios request. Intended for swagger-typescript-api SDK builders:
 *
 * `MySDK.configure({ baseURL }).enablePlugin(withBackstageAuth(identityApi)).connect()`
 *
 * Uses a generic `<T extends AxiosLikeInstance>` so the caller's precise
 * axios instance type is preserved (required to satisfy the SDK's
 * `.enablePlugin((a: AxiosInstance) => AxiosInstance)` signature).
 */
export const withBackstageAuth =
  (identityApi: IdentityApi) =>
  <T extends AxiosLikeInstance>(instance: T): T => {
    instance.interceptors.request.use(
      async (config: AxiosLikeRequestConfig) => {
        const { token } = await identityApi.getCredentials();
        if (token) {
          config.headers = {
            ...(config.headers ?? {}),
            Authorization: `Bearer ${token}`,
          };
        }
        return config;
      },
    );
    return instance;
  };

/**
 * Backend counterpart to `withBackstageAuth`: injects
 * `Authorization: Bearer <plugin-request-token>` on every axios request,
 * minted from the caller's own service credentials and targeted at
 * `targetPluginId`. Use this in backend service factories that talk to
 * another Backstage plugin — most commonly the `auth-gateway-backend` plugin
 * — where the framework's global auth middleware demands an authenticated
 * caller.
 *
 * `AuthService.getPluginRequestToken` caches short-lived tokens internally,
 * so calling it on every request stays cheap.
 *
 * Example:
 *
 * ```ts
 * MySDK.configure({ baseURL: '' })
 *   .enablePlugin(withAuthGatewayDiscovery(discovery, 'my-service'))
 *   .enablePlugin(withBackstageServiceAuth(auth, 'auth-gateway'))
 *   .enablePlugin(withJsonContentType())
 *   .connect();
 * ```
 */
export const withBackstageServiceAuth =
  (auth: BackstageServiceAuthLike, targetPluginId: string) =>
  <T extends AxiosLikeInstance>(instance: T): T => {
    instance.interceptors.request.use(
      async (config: AxiosLikeRequestConfig) => {
        const { token } = await auth.getPluginRequestToken({
          onBehalfOf: await auth.getOwnServiceCredentials(),
          targetPluginId,
        });
        config.headers = {
          ...(config.headers ?? {}),
          Authorization: `Bearer ${token}`,
        };
        return config;
      },
    );
    return instance;
  };

/**
 * Ensures POST/PUT/PATCH requests always advertise a JSON body, even when the
 * caller did not supply one.
 *
 * Axios's `transformRequest` deletes `Content-Type` when `data` is undefined
 * (a POST with no body has no media type to declare), so SDK-level
 * `headers: { 'Content-Type': 'application/json' }` defaults are silently
 * dropped on bodyless mutating requests. That produces requests with no
 * Content-Type at all, which downstream proxies (e.g. `auth-gateway-backend`)
 * re-decorate with `Content-Type: application/json` while forwarding no body,
 * leading to 415s at strict Fastify/NodeBoot upstreams.
 *
 * This interceptor substitutes an empty object for missing `data` on
 * POST/PUT/PATCH, letting axios's own logic add `Content-Type: application/json`
 * and serialize `{}` as the body. Requests that already carry data — and all
 * GET/HEAD/DELETE requests — are left untouched.
 */
export const withJsonContentType =
  () =>
  <T extends AxiosLikeInstance>(instance: T): T => {
    instance.interceptors.request.use((config: AxiosLikeRequestConfig) => {
      const method = config.method?.toLowerCase();
      if (
        (method === 'post' || method === 'put' || method === 'patch') &&
        config.data === undefined
      ) {
        config.data = {};
      }
      return config;
    });
    return instance;
  };

/**
 * Rewrites `baseURL` on every request to
 * `${discoveryApi.getBaseUrl('auth-gateway')}/${routeName}${relativeSuffix}`,
 * memoized after the first resolution. Use this for SDKs proxied through the
 * `auth-gateway-backend` plugin, where each caller is served under a named
 * route (e.g. `tech-insights`, `journeys`) mounted at
 * `/api/auth-gateway/<routeName>/...`.
 *
 * Any relative `baseURL` already set on the request (typically via a sub-SDK's
 * `.configure({ baseURL: '/lake' })`) is preserved as a suffix, so multi-SDK
 * clients like `TechInsightsClient` — which mount their sub-APIs at
 * `/lake`, `/workflows`, `/contracts`, etc. — keep routing correctly.
 *
 * Absolute `baseURL`s (starting with `http://` or `https://`) are discarded,
 * not preserved. In practice they come from swagger-typescript-api SDKs that
 * bake a fallback upstream URL into their generated code (e.g. the TDMS
 * client defaults to `https://dev.api.oneweb.mercedes-benz.com/...` when the
 * caller passes an empty `baseURL`). Preserving that fallback would let the
 * SDK bypass the gateway entirely and leak Backstage-issued tokens to the
 * upstream service. This interceptor exists precisely to route through the
 * gateway, so it always wins on absolute URLs.
 */
export const withAuthGatewayDiscovery =
  (discoveryApi: DiscoveryApi, routeName: string) =>
  <T extends AxiosLikeInstance>(instance: T): T => {
    let cached: Promise<string> | undefined;
    instance.interceptors.request.use(
      async (config: AxiosLikeRequestConfig) => {
        cached ??= (async () => {
          try {
            return await discoveryApi.getBaseUrl('auth-gateway');
          } catch (e) {
            // Evict the poisoned promise so the next request can retry
            // discovery instead of failing forever on a transient error.
            cached = undefined;
            throw e;
          }
        })();
        const gatewayBase = await cached;
        const existing = config.baseURL ?? '';
        const suffix = /^https?:\/\//i.test(existing) ? '' : existing;
        config.baseURL = `${gatewayBase}/${routeName}${suffix}`;
        return config;
      },
    );
    return instance;
  };
